import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";
import { EmptyState, Page, PageHeader, TableSkeleton } from "../../../components/ui";
import {
  QUILL_FORMATS,
  QUILL_MODULES,
} from "../../../components/editor/quillConfig";
import {
  getAboutInfo,
  postAboutDetails,
  postContactDetails,
  postTeamData,
} from "../../../services/settings";
import { readImageFile } from "../../../utils/files";
import SectionCard from "../legal/SectionCard";
import { isBlank } from "../legal/LegalTextPage";
import "../ads/ad-form.css";

/** Body saving a team member (`about` holds the about page id). */
export const teamMemberBody = (about, member) => ({
  _id: about._id,
  team_member_id: member._id,
  name: member.name,
  title: member.title,
  isImageUpdate: Boolean(member.imageBase64),
  imageBase64: member.imageBase64 || "",
  image_public_id: "",
});

/** Image picker of a form: label, chosen file name and preview. */
const ImagePicker = ({ id, label, value, onChange }) => {
  const { t } = useTranslation();
  const [name, setName] = useState("");
  return (
    <div className="legal-about-image">
      {value && <img src={value} alt="" />}
      <label htmlFor={id} className="ad-upload">
        <i className="ri-upload-2-line" aria-hidden="true"></i>
        {name || label}
      </label>
      <input
        id={id}
        type="file"
        accept=".png, .jpg, .jpeg"
        className="sr-only"
        onChange={(e) => {
          const file = e.target.files && e.target.files[0];
          if (!file) return;
          setName(file.name);
          readImageFile(file, onChange);
          e.target.value = "";
        }}
      />
      {value && (
        <button
          type="button"
          className="ui-icon-btn"
          data-tone="danger"
          title={t("Delete")}
          aria-label={t("Delete")}
          onClick={() => {
            setName("");
            onChange("");
          }}
        >
          <i className="ri-delete-bin-line" aria-hidden="true"></i>
        </button>
      )}
    </div>
  );
};

/** Section editing state: the draft being edited (null when read-only). */
function useSection(save, { valid, invalidKey, failedKey, onSaved }) {
  const { t } = useTranslation();
  const [draft, setDraft] = useState(null);
  const [saving, setSaving] = useState(false);
  const submit = async () => {
    if (!valid(draft)) {
      toast.error(t(invalidKey));
      return;
    }
    setSaving(true);
    const saved = await save(draft).catch(() => false);
    setSaving(false);
    if (!saved) {
      toast.error(t(failedKey));
      return;
    }
    await onSaved();
    setDraft(null);
    toast.success(t("Details Added Successfully!"));
  };
  return { draft, setDraft, saving, submit };
}

const AboutUs = () => {
  const { t } = useTranslation();
  const [about, setAbout] = useState(null);
  const [loading, setLoading] = useState(true);
  const refresh = () => getAboutInfo().then((data) => setAbout(data || {}));
  useEffect(() => {
    refresh().then(() => setLoading(false));
  }, []);

  const text = useSection(
    (draft) =>
      postAboutDetails({
        _id: about._id,
        about: draft.about,
        isImageUpdate: Boolean(draft.imageBase64),
        imageBase64: draft.imageBase64,
      }),
    {
      valid: (draft) => !isBlank(draft.about),
      invalidKey: "Please Fill About Us Completely",
      failedKey: "Adding About Us Failed!",
      onSaved: refresh,
    }
  );

  const contact = useSection(
    (draft) => postContactDetails({ _id: about._id, ...draft }),
    {
      valid: (draft) => Object.values(draft).every((value) => value.trim()),
      invalidKey: "Please Fill Contact Details Completely",
      failedKey: "Adding Contact Details Failed!",
      onSaved: refresh,
    }
  );

  const team = useSection(
    async (draft) => {
      const results = await Promise.all(
        draft.map((member) => postTeamData(teamMemberBody(about, member)))
      );
      return results.every(Boolean);
    },
    {
      valid: (draft) =>
        draft.every((member) => member.name.trim() && member.title.trim()),
      invalidKey: "Please Fill Name and Title for Each Member",
      failedKey: "Adding Team Members Failed!",
      onSaved: refresh,
    }
  );

  if (loading) {
    return (
      <Page>
        <PageHeader title={t("About Us")} />
        <section className="ui-card">
          <TableSkeleton rows={6} />
        </section>
      </Page>
    );
  }

  const members = about.team_members || [];
  const setMember = (index, field, value) =>
    team.setDraft((current) =>
      current.map((member, i) => (i === index ? { ...member, [field]: value } : member))
    );

  return (
    <Page>
      <PageHeader title={t("About Us")} />

      <SectionCard
        title={t("Presentation")}
        editing={Boolean(text.draft)}
        saving={text.saving}
        onEdit={() => text.setDraft({ about: about.about || "", imageBase64: "" })}
        onCancel={() => text.setDraft(null)}
        onSave={text.submit}
      >
        {text.draft ? (
          <>
            <ReactQuill
              className="ui-editor"
              modules={QUILL_MODULES}
              formats={QUILL_FORMATS}
              value={text.draft.about}
              onChange={(value) => text.setDraft((d) => ({ ...d, about: value }))}
            />
            <ImagePicker
              id="about-image"
              label={t("About Image")}
              value={text.draft.imageBase64}
              onChange={(value) => text.setDraft((d) => ({ ...d, imageBase64: value }))}
            />
          </>
        ) : about.about ? (
          <div className="legal-html" dangerouslySetInnerHTML={{ __html: about.about }} />
        ) : (
          <EmptyState icon="ri-information-line">{t("Not filled yet")}</EmptyState>
        )}
      </SectionCard>

      <SectionCard
        title={t("Contact Details")}
        editing={Boolean(contact.draft)}
        saving={contact.saving}
        onEdit={() =>
          contact.setDraft({
            address: about.address || "",
            phone: about.phone || "",
            email: about.email || "",
          })
        }
        onCancel={() => contact.setDraft(null)}
        onSave={contact.submit}
      >
        {contact.draft ? (
          <div className="ui-form">
            {[
              { field: "address", label: t("Address"), type: "text" },
              { field: "phone", label: t("Phone Number"), type: "tel" },
              { field: "email", label: t("Email Address"), type: "email" },
            ].map(({ field, label, type }) => (
              <div key={field} className="ui-field">
                <label htmlFor={`contact-${field}`}>{label}</label>
                <input
                  id={`contact-${field}`}
                  className="ui-input"
                  type={type}
                  value={contact.draft[field]}
                  onChange={(e) => {
                    const { value } = e.target;
                    contact.setDraft((d) => ({ ...d, [field]: value }));
                  }}
                />
              </div>
            ))}
          </div>
        ) : (
          <dl className="legal-facts">
            <div>
              <dt>{t("Address")}</dt>
              <dd>{about.address || "—"}</dd>
            </div>
            <div>
              <dt>{t("Phone Number")}</dt>
              <dd>{about.phone || "—"}</dd>
            </div>
            <div>
              <dt>{t("Email Address")}</dt>
              <dd>{about.email || "—"}</dd>
            </div>
          </dl>
        )}
      </SectionCard>

      <SectionCard
        title={t("Team Members")}
        editing={Boolean(team.draft)}
        saving={team.saving}
        onEdit={
          members.length > 0
            ? () =>
                team.setDraft(
                  members.map((member) => ({
                    _id: member._id,
                    name: member.name || "",
                    title: member.title || "",
                    imageBase64: "",
                  }))
                )
            : undefined
        }
        onCancel={() => team.setDraft(null)}
        onSave={team.submit}
      >
        {team.draft ? (
          team.draft.map((member, index) => (
            <div key={member._id || index} className="legal-member">
              <span className="legal-team-avatar" aria-hidden="true">
                {index + 1}
              </span>
              <div className="ui-field">
                <label htmlFor={`member-name-${index}`}>{t("name")}</label>
                <input
                  id={`member-name-${index}`}
                  className="ui-input"
                  value={member.name}
                  onChange={(e) => setMember(index, "name", e.target.value)}
                />
              </div>
              <div className="ui-field">
                <label htmlFor={`member-title-${index}`}>{t("Title")}</label>
                <input
                  id={`member-title-${index}`}
                  className="ui-input"
                  value={member.title}
                  onChange={(e) => setMember(index, "title", e.target.value)}
                />
              </div>
              <ImagePicker
                id={`member-image-${index}`}
                label={t("Image")}
                value={member.imageBase64}
                onChange={(value) => setMember(index, "imageBase64", value)}
              />
            </div>
          ))
        ) : members.length > 0 ? (
          <ul className="legal-team">
            {members.map((member, index) => (
              <li key={member._id || index}>
                <span className="legal-team-avatar" aria-hidden="true">
                  {(member.name || "?").charAt(0).toUpperCase()}
                </span>
                <div>
                  <strong>{member.name || "—"}</strong>
                  <span>{member.title || "—"}</span>
                </div>
              </li>
            ))}
          </ul>
        ) : (
          <EmptyState icon="ri-group-line">{t("Not filled yet")}</EmptyState>
        )}
      </SectionCard>
    </Page>
  );
};

export default AboutUs;
